import type { PayloadAction } from "@reduxjs/toolkit";
import { createSlice } from "@reduxjs/toolkit";
import { APP_SERVICES_NAME } from "./constants";
import {
  addChildToStorageObjectsReducerBuilder,
  addStorageObjectsReducerBuilder,
  createStorageObjectSharingReducerBuilder,
  createStorageObjectVersionReducerBuilder,
  deleteStorageObjectSharingReducerBuilder,
  deleteStorageObjectsReducerBuilder,
  fetchAllStorageObjectsReducerBuilder,
  fetchAllStorageObjectsSharingReducerBuilder,
  fetchAllStorageObjectVersionsReducerBuilder,
  fetchStorageObjectByIdReducerBuilder,
  removeChildFromStorageObjectsReducerBuilder,
  updateStorageObjectSharingReducerBuilder,
  updateStorageObjectsReducerBuilder,
  updateStorageObjectVersionReducerBuilder,
} from "./reducers";
import type {
  RemoveOptimisticPayload,
  StorageServiceVersions,
  TSetNavigatedObjectIDPayload,
  TSetSelectedObjectIDPayload,
} from "./types";
import {
  generateStorageInitialState,
  makeInitialState,
} from "./utils/generateStorageInitialState";
import {
  StorageObjectValue,
  StorageScope,
} from "../../../services/api/instances/storageApi/types/service_types";
import { storageObjectAdapter } from "./adapter";

const storageObjectSlice = createSlice({
  name: "storage",
  initialState: generateStorageInitialState(APP_SERVICES_NAME),
  reducers: {
    setSelectedObjectId: (
      state,
      action: PayloadAction<TSetSelectedObjectIDPayload>,
    ) => {
      const { service, id } = action.payload;
      if (!id) return;
      state[service].selectedId = id;
    },
    setCurrentService: (state, action: PayloadAction<StorageScope | null>) => {
      state.currentService = action.payload;
    },
    setMutationFlag: (
      state,
      action: PayloadAction<{ service: StorageScope; hasMutation: boolean }>,
    ) => {
      const { service, hasMutation } = action.payload;
      state[service].hasMutation = hasMutation;
    },
    sortParentChildren: (state, action) => {},
    setSelectedVersionId: (
      state,
      action: PayloadAction<{
        service: StorageScope;
        version_id: string | null;
      }>,
    ) => {
      const { service, version_id } = action.payload;
      state[service].selectedVersionId = version_id;
    },
    setObjectVersions: (
      state,
      action: PayloadAction<{
        service: StorageScope;
        versions: StorageServiceVersions;
      }>,
    ) => {
      const { service, versions } = action.payload;
      state[service].versions = versions;
    },
    removeStorageObjectLocal: (
      state,
      action: PayloadAction<RemoveOptimisticPayload<StorageObjectValue>>,
    ) => {
      const { service, id, object } = action.payload;

      if (!state[service]) return;

      const entityState = state[service].data;

      delete entityState.entities[id];
      entityState.ids = entityState.ids.filter((itemId) => itemId !== id);

      if (object.parentId && entityState.entities[object.parentId]) {
        const parent = entityState.entities[object.parentId];
        if (parent?.childrenIds) {
          parent.childrenIds = parent.childrenIds.filter(
            (childId) => childId !== id,
          );
        }
      }
    },
    storageObjectParentReset: (
      state,
      action: PayloadAction<{
        service: StorageScope;
        id: string;
        parentId: string;
      }>,
    ) => {
      const { service, id, parentId } = action.payload;
      const childrenIds = state[service].data.entities[parentId].childrenIds;
      storageObjectAdapter.updateOne(state[service].data, {
        id: id,
        changes: { parentId: null },
      });
      storageObjectAdapter.updateOne(state[service].data, {
        id: parentId,
        changes: {
          childrenIds: childrenIds.filter((childId) => childId !== id),
        },
      });
    },
    setIsServiceLoading: (
      state,
      action: PayloadAction<{ service: StorageScope; value: boolean }>,
    ) => {
      const { service, value } = action.payload;
      state[service].loading = value;
    },
    clearServiceState: (
      state,
      action: PayloadAction<{ service: StorageScope }>,
    ) => {
      const { service } = action.payload;
      state[service] = makeInitialState();
    },
  },
  extraReducers: (builder) => {
    fetchAllStorageObjectsReducerBuilder(builder);
    fetchStorageObjectByIdReducerBuilder(builder);
    addStorageObjectsReducerBuilder(builder);
    updateStorageObjectsReducerBuilder(builder);
    deleteStorageObjectsReducerBuilder(builder);
    addChildToStorageObjectsReducerBuilder(builder);
    fetchAllStorageObjectVersionsReducerBuilder(builder);
    createStorageObjectVersionReducerBuilder(builder);
    updateStorageObjectVersionReducerBuilder(builder);
    removeChildFromStorageObjectsReducerBuilder(builder);
    fetchAllStorageObjectsSharingReducerBuilder(builder);
    createStorageObjectSharingReducerBuilder(builder);
    updateStorageObjectSharingReducerBuilder(builder);
    deleteStorageObjectSharingReducerBuilder(builder);
  },
});

export const storageObjectReducers = storageObjectSlice.reducer;

export const {
  setSelectedObjectId,
  setCurrentService,
  setSelectedVersionId,
  setMutationFlag,
  setObjectVersions,
  removeStorageObjectLocal,
  storageObjectParentReset,
  setIsServiceLoading,
  clearServiceState,
} = storageObjectSlice.actions;
